import { timeDiff } from '@csp/csp-common-date-util';
import { useCallback, useEffect, useState } from 'react';

type KeydownEvent = {
  key: string;
  timestamp: Date;
};

type In = {
  onBarcodeIdDetected(value: string): void;
  isDisabled?: boolean;
};

const MIN_CHARS = 5;
const SCAN_TIME_THRESHOLD_IN_SECONDS = 1;

export const useBarcodeScanningDetection = ({ onBarcodeIdDetected, isDisabled }: In): void => {
  const [keydownEvents, setKeydownEvents] = useState<KeydownEvent[]>([]);

  const handleDetection = useCallback((): void => {
    const barcodeId = keydownEvents.map(event => event.key).join('');
    onBarcodeIdDetected(barcodeId);
    setKeydownEvents([]);
  }, [onBarcodeIdDetected, setKeydownEvents, keydownEvents]);

  const detect = useCallback(
    (event: KeyboardEvent): void => {
      const isTargetInputElement =
        !!(event.target as HTMLElement).matches && (event.target as HTMLElement).matches('input');
      if (!isTargetInputElement) {
        const isEventWithinScanTimeThreshold =
          keydownEvents?.length &&
          keydownEvents[0] &&
          timeDiff(keydownEvents[0].timestamp.getTime()).asRoundedSeconds < SCAN_TIME_THRESHOLD_IN_SECONDS;
        if (event.key === 'Enter') {
          const isBarcodeScan = keydownEvents.length >= MIN_CHARS && isEventWithinScanTimeThreshold;
          if (isBarcodeScan) {
            handleDetection();
            event.stopPropagation();
            event.preventDefault();
          }
        } else {
          const newEvent: KeydownEvent = { key: event.key, timestamp: new Date() };
          setKeydownEvents(current => (isEventWithinScanTimeThreshold ? [...current, newEvent] : [newEvent]));
        }
      }
    },
    [keydownEvents, handleDetection, setKeydownEvents],
  );

  useEffect(() => {
    if (!isDisabled) {
      window.addEventListener('keydown', detect);
    }

    return (): void => window.removeEventListener('keydown', detect);
  }, [detect, isDisabled]);
};
